import { action } from "typesafe-actions";
import { AnyAction } from "redux";
import { ThunkAction } from "redux-thunk";
import { IStoreState } from "../initialStoreState";
import { api } from "../../api/api";
import { ISupportTicket } from "./support.types";
import {
  saveLoaderCompleted,
  saveLoaderProgress,
  showMessage,
} from "../messages/messagesActions";
import { IDataTableV2DateState } from "../../components/TableV2/preDefinedPlugins/DataTableV2Date/DataTableV2Date.types";
import { IDatatableV2AdvancedSearchFilter } from "../../components/TableV2/preDefinedPlugins/SearchFilter/SearchFilter.types";

// Action Types
export const FETCH_SUPPORT_TICKET_LIST_PROGRESS =
  "FETCH_SUPPORT_TICKET_LIST_PROGRESS";
export const FETCH_SUPPORT_TICKET_LIST_SUCCESS =
  "FETCH_SUPPORT_TICKET_LIST_SUCCESS";
export const FETCH_SUPPORT_TICKET_LIST_FAILED =
  "FETCH_SUPPORT_TICKET_LIST_FAILED";

export const FETCH_SUPPORT_TICKET_PROGRESS = "FETCH_SUPPORT_TICKET_PROGRESS";
export const FETCH_SUPPORT_TICKET_SUCCESS = "FETCH_SUPPORT_TICKET_SUCCESS";
export const FETCH_SUPPORT_TICKET_FAILED = "FETCH_SUPPORT_TICKET_FAILED";

// Action Creators
export const fetchSupportTicketListProgress = () =>
  action(FETCH_SUPPORT_TICKET_LIST_PROGRESS);
export const fetchSupportTicketListSuccess = (
  data: ISupportTicket[],
  totalRecords: number
) => action(FETCH_SUPPORT_TICKET_LIST_SUCCESS, { data, totalRecords });
export const fetchSupportTicketListFailed = () =>
  action(FETCH_SUPPORT_TICKET_LIST_FAILED);

export const fetchSupportTicketProgress = () =>
  action(FETCH_SUPPORT_TICKET_PROGRESS);
export const fetchSupportTicketSuccess = (data: ISupportTicket) =>
  action(FETCH_SUPPORT_TICKET_SUCCESS, { data });
export const fetchSupportTicketFailed = () =>
  action(FETCH_SUPPORT_TICKET_FAILED);

// Async Actions
export const fetchSupportTicketListAsync =
  (
    pageNumber: number,
    rowsInPerPage: number,
    // limit: number,
    status: string,
    // date?: IDataTableV2DateState["dates"],
    searchValue?: IDatatableV2AdvancedSearchFilter,
    searchByTitle?: string,
    companyFilter?: string | null
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch, getState) => {
    try {
      let searchQuery = "";

      if ((searchValue?.length ?? 0) > 0 || searchByTitle || companyFilter) {
        const advanceFilter = [...(searchValue || [])];

        if (searchByTitle) {
          advanceFilter.push({
            column: ["title"],
            value: searchByTitle,
            logicalOperator: "AND",
            operator: "CONTAINS",
          });
        }

        if (companyFilter) {
          advanceFilter.push({
            column: ["company_outlet_uuid"],
            value: companyFilter,
            logicalOperator: "AND",
            operator: "EQUALS",
          });
        }

        searchQuery += `&advanceFilter=${JSON.stringify(advanceFilter)}`;
      }

      // if (date) {
      //   searchQuery += `&from_date=${date.fromDate}&to_date=${date.toDate}`;
      // }

      if (status && status !== "-1") {
        searchQuery += `&status=${status}`;
      }

      const finalUrl =
        `/support/get-support-tickets?pageNo=${pageNumber}&itemPerPage=${rowsInPerPage}` +
        searchQuery;

      dispatch(fetchSupportTicketListProgress());
      dispatch(saveLoaderProgress());
      const res = await api.get(finalUrl);
      const data: ISupportTicket[] = res.data.data;
      const totalRecords = res.data.totalRecords;

      dispatch(fetchSupportTicketListSuccess(data, totalRecords));
    } catch (err: any) {
      dispatch(fetchSupportTicketListFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to fetch support tickets",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const fetchSupportTicketAsync =
  (supportTicketUuid: string): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    try {
      dispatch(fetchSupportTicketProgress());
      // dispatch(saveLoaderProgress());
      const res = await api.get(
        `/support/get-support-tickets?support_tickets_uuid=${supportTicketUuid}`
      );
      const data = res.data.data;
      if (data.length > 0) {
        dispatch(fetchSupportTicketSuccess(data[0]));
      } else {
        dispatch(fetchSupportTicketFailed());
      }
    } catch (err: any) {
      dispatch(fetchSupportTicketFailed());
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to fetch support ticket",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const upsertSupportTicketAsync =
  (
    supportTicket: ISupportTicket,
    onCallback: (isSuccess?: boolean, data?: ISupportTicket) => void
  ): ThunkAction<void, IStoreState, {}, AnyAction> =>
  async (dispatch) => {
    // dispatch(saveLoaderProgress());
    try {
      const {
        support_tickets_unique_id,
        create_ts,
        insert_ts,
        created_by_uuid,
        created_by_name,
        modified_by_uuid,
        modified_by_name,
        ...rest
      } = supportTicket;

      const res = await api.post("/support/upsert-support-tickets", rest);
      dispatch(
        showMessage({
          type: "success",
          message: res.data.message,
          displayAs: "snackbar",
        })
      );
      const data = res.data.data;
      onCallback(true, data);
    } catch (err: any) {
      onCallback(false);
      dispatch(
        showMessage({
          type: "error",
          message:
            err?.response?.data?.message || "Failed to save support ticket",
          displayAs: "snackbar",
        })
      );
    } finally {
      dispatch(saveLoaderCompleted());
    }
  };

export const CLEAR_SUPPORT_TICKET_STATE = "CLEAR_SUPPORT_TICKET_STATE";
export const CLEAR_SUPPORT_TICKET = "CLEAR_SUPPORT_TICKET";

export const clearSupportTicketState = () => action(CLEAR_SUPPORT_TICKET_STATE);
export const clearSupportTicket = () => action(CLEAR_SUPPORT_TICKET);
